import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Input,
  FormHelperText,
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import DashboardHeader from '../components/DashboardHeader';
import { Company } from '../types';
import { companyService } from '../services/companyService';
import { useAuth } from '../contexts/AuthContext';

export const CompaniesPage: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Form states
  const [formData, setFormData] = useState({
    company_id: '',
    name: '',
    pin: '',
    first_day_of_week: 'Monday',
    manager_id: '',
    phone: '',
    image_url: '',
  });
  
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const data = await companyService.getCompanies();
      setCompanies(data);
      setError(null);
    } catch (err) {
      setError('Error al cargar las empresas');
      console.error('Error loading companies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = () => {
    setFormData({
      company_id: '',
      name: '',
      pin: '',
      first_day_of_week: 'Monday',
      manager_id: '',
      phone: '',
      image_url: '',
    });
    setSelectedImage(null);
    setImagePreview(null);
    setShowCreateDialog(true);
  };

  const handleEditCompany = (company: Company) => {
    setFormData({
      company_id: company.company_id || '',
      name: company.name,
      pin: company.pin || '',
      first_day_of_week: company.first_day_of_week,
      manager_id: company.manager_id || '',
      phone: company.phone || '',
      image_url: company.image_url || '',
    });
    setSelectedImage(null);
    // Mostrar la imagen guardada si existe
    setImagePreview(company.image_url || null);
    setSelectedCompany(company);
    setShowEditDialog(true);
  };

  const handleDeleteCompany = async (company: Company) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar la empresa "${company.name}"?`)) {
      try {
        await companyService.deleteCompany(company.id);
        await loadCompanies();
      } catch (error) {
        console.error('Error deleting company:', error);
        const anyErr: any = error as any;
        if (anyErr?.response?.status === 403) {
          setError('No tienes permisos para eliminar empresas.');
        } else if (anyErr?.response?.data?.message) {
          setError(anyErr.response.data.message);
        } else {
          setError('Error al eliminar la empresa');
        }
      }
    }
  };

  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      
      img.onload = () => {
        // Calcular nuevas dimensiones (máximo 800x600)
        const maxWidth = 800;
        const maxHeight = 600;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Dibujar imagen redimensionada
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convertir a base64 con calidad 0.8
        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        resolve(compressedDataUrl);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        setError('Por favor selecciona un archivo de imagen válido');
        return;
      }
      
      // Validar tamaño (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('La imagen debe ser menor a 5MB');
        return;
      }

      setSelectedImage(file);
      setError(null);
      
      try {
        // Comprimir imagen
        const compressedImage = await compressImage(file);
        setImagePreview(compressedImage);
      } catch (error) {
        console.error('Error comprimiendo imagen:', error);
        setError('Error al procesar la imagen');
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const companyData = { ...formData };
      
      // Si hay una imagen seleccionada nueva, usar la versión comprimida
      if (selectedImage && imagePreview) {
        companyData.image_url = imagePreview;
      } else if (imagePreview) {
        // Si solo hay imagePreview (imagen existente), mantenerla
        companyData.image_url = imagePreview;
      }
      
      console.log('Datos a enviar:', companyData);
      
      // Guardar la empresa
      await saveCompany(companyData);
    } catch (error) {
      console.error('Error saving company:', error);
      setError('Error al guardar la empresa');
    }
  };

  const saveCompany = async (companyData: any) => {
    try {
      if (showEditDialog && selectedCompany) {
        // Actualizar empresa existente (NO enviar company_id)
        const updateData = {
          name: companyData.name,
          first_day_of_week: companyData.first_day_of_week,
          manager_id: companyData.manager_id || null,
          phone: companyData.phone,
          image_url: companyData.image_url,
        };
        console.log('Enviando datos de actualización:', updateData);
        await companyService.updateCompany(selectedCompany.id, updateData);
      } else {
        // Crear nueva empresa
        const createData = {
          company_id: companyData.company_id || undefined,
          name: companyData.name,
          pin: companyData.pin,
          first_day_of_week: companyData.first_day_of_week,
          manager_id: companyData.manager_id || null,
          phone: companyData.phone,
          image_url: companyData.image_url,
        };
        console.log('Enviando datos de creación:', createData);
        await companyService.createCompany(createData);
      }

      setShowCreateDialog(false);
      setShowEditDialog(false);
      setSelectedCompany(null);
      setSelectedImage(null);
      setImagePreview(null);
      await loadCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
      setError('Error al guardar la empresa');
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <DashboardHeader title="Empresas" subtitleChips={["Super Admin"]} />
        <Box sx={{ p: 3 }}>
          <Typography>Cargando empresas...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#f7f9fc', p: { xs: 2, md: 4 } }}>
      <DashboardHeader title="Empresas" subtitleChips={["Super Admin"]} showNav currentPage="companies" />
      
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              Gestión de Empresas
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Administra y gestiona todas las empresas del sistema
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateCompany}
            sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
          >
            + CREAR NUEVA EMPRESA
          </Button>
        </Box>

        {/* Búsqueda compacta con ícono */}
        <Box sx={{ mb: 3, maxWidth: 500 }}>
          <TextField
            placeholder="Buscar empresa por nombre o ID"
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center', pl: 1 }}>
                  <span style={{
                    width: 16,
                    height: 16,
                    display: 'inline-block',
                    background: 'linear-gradient(135deg, #5ac8fa 0%, #a855f7 100%)',
                    WebkitMask: 'url("data:image/svg+xml;utf8,<?xml version=\'1.0\' encoding=\'UTF-8\'?><svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'><path fill=\'%23000\' d=\'M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z\'/></svg>") center / contain no-repeat',
                    mask: 'url("data:image/svg+xml;utf8,<?xml version=\'1.0\' encoding=\'UTF-8\'?><svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'><path fill=\'%23000\' d=\'M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z\'/></svg>") center / contain no-repeat'
                  }} />
                </Box>
              ),
              sx: { borderRadius: 5, backgroundColor: 'white', pl: 0.5 }
            }}
          />
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Companies Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
                             <TableHead>
                 <TableRow>
                   <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                   <TableCell sx={{ fontWeight: 'bold' }}>Nombre</TableCell>
                   <TableCell sx={{ fontWeight: 'bold' }}>PIN</TableCell>
                   <TableCell sx={{ fontWeight: 'bold' }}>Teléfono</TableCell>
                   <TableCell sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                 </TableRow>
               </TableHead>
              <TableBody>
                {companies.map((company) => (
                                     <TableRow key={company.id} hover>
                     <TableCell>{company.company_id}</TableCell>
                     <TableCell>
                       <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                         {company.name}
                       </Typography>
                     </TableCell>
                     <TableCell>{company.pin ?? '-'}</TableCell>
                     <TableCell>
                       {company.phone || '-'}
                     </TableCell>
                     <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditCompany(company)}
                          sx={{ color: '#2196f3' }}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteCompany(company)}
                          sx={{ color: '#f44336' }}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Create/Edit Company Dialog */}
      <Dialog open={showCreateDialog || showEditDialog} onClose={() => {
        setShowCreateDialog(false);
        setShowEditDialog(false);
        setSelectedCompany(null);
      }} maxWidth="md" fullWidth>
        <DialogTitle>
          {showEditDialog ? 'Editar Empresa' : 'Crear Nueva Empresa'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
            {/* Columna Izquierda */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
              <TextField
                label="ID de Empresa *"
                placeholder="ID"
                value={formData.company_id}
                onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                fullWidth
                required
                disabled={showEditDialog} // Solo lectura en edición
                helperText={showEditDialog ? "El ID no se puede modificar" : ""}
              />

              <TextField
                label="Nombre de Empresa *"
                placeholder="Nombre"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                fullWidth
                required
              />

              <TextField
                label="PIN de Empresa"
                placeholder="PIN"
                type="password"
                value={formData.pin}
                onChange={(e) => setFormData({ ...formData, pin: e.target.value })}
                fullWidth
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />

              <FormControl fullWidth required>
                <InputLabel>Primer día de semana *</InputLabel>
                <Select
                  value={formData.first_day_of_week}
                  label="Primer día de semana *"
                  onChange={(e) => setFormData({ ...formData, first_day_of_week: e.target.value })}
                >
                  <MenuItem value="Monday">Lunes</MenuItem>
                  <MenuItem value="Tuesday">Martes</MenuItem>
                  <MenuItem value="Wednesday">Miércoles</MenuItem>
                  <MenuItem value="Thursday">Jueves</MenuItem>
                  <MenuItem value="Friday">Viernes</MenuItem>
                  <MenuItem value="Saturday">Sábado</MenuItem>
                  <MenuItem value="Sunday">Domingo</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Jefe de empresa"
                placeholder="ID del jefe"
                value={formData.manager_id}
                onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
                fullWidth
              />

              <TextField
                label="Teléfono de empresa"
                placeholder="Teléfono"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                fullWidth
              />
            </Box>

                         {/* Columna Derecha */}
             <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
               <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Logo de la Empresa
                </Typography>
                
                {/* Botón para seleccionar archivo */}
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  {selectedImage ? selectedImage.name : 'Seleccionar Imagen'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageSelect}
                    ref={fileInputRef}
                  />
                </Button>

                {/* Botón para limpiar imagen */}
                {(selectedImage || imagePreview) && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                      setSelectedImage(null);
                      setImagePreview(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    sx={{ mb: 2 }}
                  >
                    Limpiar Imagen
                  </Button>
                )}

                {/* Preview de la imagen */}
                {imagePreview && (
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '150px',
                        objectFit: 'contain',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                  </Box>
                )}

                <FormHelperText>
                  Formatos permitidos: JPG, PNG, GIF. Máximo 5MB.
                </FormHelperText>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setShowCreateDialog(false);
            setShowEditDialog(false);
            setSelectedCompany(null);
          }}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
